import React, { useState, useEffect, useCallback } from "react";
import { Button } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import BCNavbar from "./BCNavbar";
import BCSidebar from "./BCSidebar";
import BCMessage from "./ui/BCMessage";
import { getTenantMetadata, getUser, patchUser } from "../api/GlobalApi";
import { getMockProfile, getMockProfileAsset } from "../api/mock/MockBrokerApi";
import { getProfile, getProfileAsset } from "../api/BrokerApi";
import { createErrorResponse, createFile, handleErrorResponse, handleSpaFwEventLog, isWhiteLabeled } from "../Helpers";
import "../styles/main.scss";
import "../styles/ui.scss";
import BCModal from "./ui/BCModal";
import { getMockTenantMetadata, patchMockUser } from "../api/mock/MockGlobalApi";
import { Environments, GlobalConstants, MsalConstants, Roles, Sites } from "./constants/GlobalConstants";
import { getEnvironmentDomain, getSubdomainFromUrl, handleCreateAuthCookies, isJsonString, logout, handleApiPromise } from "../helpers/GlobalHelper";
import { useMsal } from "@azure/msal-react";
import { useBCGlobalMetadataReducer } from "./BCGlobalMetadata";
import { FeatureFlags } from "./constants/LocalServerConstants";
import { getIsWhiteLabeled, getSite } from "../helpers/PermissionHelper";

const BCLayout = ({
  isGreaterThanTablet,
  isGreaterThanMobile,
  isSidebar,
  setIsSidebar,
  isSidebarToggle,
  setIsSidebarToggle,
  user,
  setUser,
  setProxyUser,
  removeCookie,
  children,
  setIsMessage,
  setErrorResponse,
  proxyUser,
  permissions,
  setPermissions,
  successMessage,
  setSuccessMessage,
  isModalOpen,
  setIsModalOpen,
  setIsW9MessageVisible,
  setBrokerColor,
  setTenantLogo,
  isCreateNewUser,
  setIsCreateNewUser,
  newAvatarFile,
  brokerColor,
  isUserProfileEdit,
  isUserProfileUpdate,
  setSubdomain,
  setCookie
}) => {
  const { instance } = useMsal();
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isProfileLoading, setIsProfileLoading] = useState(true as boolean);
  const [portalPermissionsList, setPortalPermissionsList] = useState([] as string[]);
  const [profile, setProfile] = useState(undefined as any);
  const [completedW9, setCompletedW9] = useState(false as boolean);
  const [isFTUE, setFTUE] = useState(true as boolean);
  const dispatch = useBCGlobalMetadataReducer()[1];
  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleRouteToBrokerages = useCallback(() => {
    history.push("/brokerages");
  }, [history]);

  const handeGetProfileAssetAsync = useCallback(async (id) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockProfileAsset(id) : await getProfileAsset(id) as any;
      if (response && response.ok) {
        const data = await response.blob();
        const avatarFile = await createFile(data, "profile-avatar", data.type);
        return URL.createObjectURL(avatarFile);
      }
      else {
        createErrorResponse("Error", "Caught Error while trying to get Profile Asset", undefined, "user/asset/${" + id + "id}", undefined, undefined, "GET", response);
        setIsMessage(true);
      }
    }
    catch (error: any) {
      createErrorResponse("Error", "Caught Error while trying to get Profile Asset", undefined, "user/asset/${" + id + "}", undefined, error.status, "GET", error);
      setErrorResponse(error.status);
      setIsMessage(false);
    }
  }, [setIsMessage]);

  const handleWelcomeMessage = () => {
    setIsModalOpen(false);
    setFTUE(false);
  };

  useEffect(() => {
    const getProfileAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockProfile() : await getProfile();
        if (response && response.ok) {
          const profile = await response.json() as any;
          if (profile.id) {
            return profile;
          }
        }
        else {
          createErrorResponse("Error", "Caught Error while trying to generate profile", undefined, "user/profile", undefined, undefined, "GET", response);
        }
      }
      catch (error: any) {
        createErrorResponse("Error", "Caught Error while trying to generate profile", undefined, "user/profile", undefined, error.status, "GET", error);
      }
    };

    const getTenantMetadataAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockTenantMetadata() : await getTenantMetadata() as any;
        if (response && response.ok) {
          const tenantMetadata = await response.json() as any;
          if (tenantMetadata.themeColor) {
            setBrokerColor(getIsWhiteLabeled() ? tenantMetadata.themeColor : undefined);
          }
        }
        else {
          createErrorResponse("Error", "Caught Error while trying to get Tenant Metadata", undefined, "user/tenant-metadata", undefined, undefined, "GET", response);
        }
      }
      catch (error: any) {
        createErrorResponse("Error", "Caught Error while trying to Tenant Metadata", undefined, "user/tenant-metadata", undefined, error.status, "GET", error);
      }
    };

    const patchUserAsync = async (patchObj) => {
      try {
        const response = FeatureFlags.IS_MOCK ? await patchMockUser(patchObj) : await patchUser(patchObj);
        if (response && response.ok) {
          return true;
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error: any) {
        handleSpaFwEventLog("Caught error while trying to patch user", setIsMessage, setErrorResponse);  
      }
  
      return false;
    };

    const handlePatchUserAsync = async (profile, portal) => {
      let preferences = profile?.preferences || {};
      if (isJsonString(preferences)) {
        preferences = JSON.parse(preferences);
      }
      
      preferences.portal = portal;
      await patchUserAsync({
        id: profile.id,
        preferences: JSON.stringify(preferences)
      });
    };

    const handleNavbarPermissionsAsync = async (profile) => {
      let portalCount = 0;
      const portalPermissions = [] as string[];
      const brokerPortalPermission = profile.permissions.find(x => x.name === "BrokerPortal");
      const lmsPortalPermission = profile.permissions.find(x => x.name === "LMSVersion2");
      // const loanOfficerPortalPermission = profile.permissions.find(x => x.name === "LoanOfficerPortal");
      if (lmsPortalPermission) {
        portalPermissions.push(GlobalConstants.Portals.LMS);
        portalCount++;
      }

      if (brokerPortalPermission) {
        portalPermissions.push(GlobalConstants.Portals.BROKER);
        portalCount++;
      }

      // TODO: Review this portal when leadership wants it
      // if (loanOfficerPortalPermission && process.env.REACT_APP_ENV === Environments.LOCAL) {
      //   portalPermissions.push(GlobalConstants.Portals.LOAN_OFFICER);
      //   portalCount++;
      // }

      if (portalCount === 0) {
        logout(instance, removeCookie);
      }
      else if (isWhiteLabeled(profile.permissions)) {
        await getTenantMetadataAsync();
      }

      if (portalPermissions.length > 0) {
        setPortalPermissionsList(portalPermissions);
      }
    };

    const getUserAsync = async () => {
      setIsMessage(false);
      try {
        const user = await getUser();
        const profile = await getProfileAsync();
        if (profile) {
          user.profileId = profile.id;
          user.name = `${profile.firstName} ${profile.lastName}`;
          user.channels = profile.channels;
          user.persona = profile.persona;
          user.channelId = profile.channelId;
          user.organization = profile.organization;
          if (profile.avatarAssetId) {
            const avatarFileUrl = await handeGetProfileAssetAsync(profile.avatarAssetId);
            if (avatarFileUrl) {
              user.avatar = avatarFileUrl;
            }
          }

          setPermissions(profile.permissions);
          setProfile(profile);
          let preferences = profile?.preferences;
          if (preferences && isJsonString(preferences)) {
            preferences = JSON.parse(profile?.preferences);
          }
          else if (preferences && !isJsonString(preferences)) {
            preferences = null;
          }

          user.preferences = preferences;
          if (preferences?.proxyId && isFirstLoad) {
            const proxyProfile = await handleApiPromise(
              () => getProfile(preferences?.proxyId),
              () => getMockProfile(),
              (response) => handleErrorResponse(response, setIsMessage, setErrorResponse),
              true,
              {}
            ) || {};

            setProxyUser(proxyProfile);
          }

          if (profile.id && (getSite() !== Sites.Hosts.MY_BUILDERS_CAPITAL || profile.isExternalUser)) {
            if (!preferences || preferences && preferences?.portal !== GlobalConstants.Portals.BROKER) {
              await handlePatchUserAsync(profile, GlobalConstants.Portals.BROKER);
            }
            
            user.portal = GlobalConstants.Portals.BROKER;
          }
          else if (getSite() === Sites.Hosts.MY_BUILDERS_CAPITAL && profile.id && !preferences) {
            if (profile.persona === Roles.OTHER_INTERNAL_USER) {
              await handlePatchUserAsync(profile, GlobalConstants.Portals.LMS);
              user.portal = GlobalConstants.Portals.LMS;
            }
            else {
              await handlePatchUserAsync(profile, GlobalConstants.Portals.BROKER);
              user.portal = GlobalConstants.Portals.BROKER;
            }

            await handleNavbarPermissionsAsync(profile);
          }
          else if (getSite() === Sites.Hosts.MY_BUILDERS_CAPITAL && preferences && preferences.portal) {
            user.portal = preferences?.portal;
            await handleNavbarPermissionsAsync(profile);
          }
          else {
            handleSpaFwEventLog("Caught error while reading profile preferences", setIsMessage, setErrorResponse); 
          }

          user.isExternalUser = profile.isExternalUser ? true : false;
          user.preferredLoanOfficerOrganizationContactId = profile.preferredLoanOfficerOrganizationContactId || "";

          if (profile.organizationContactId) {
            user.organizationContactId = profile.organizationContactId;
          }

          if (profile.organizations && profile.organizations[0] && profile.organizations[0].logoAssetId) {
            setTenantLogo(profile.organizations[0].logoAssetId);
          }

          if (profile.organization && profile.organization.subdomain) {
            setSubdomain(profile.organization.subdomain);
          }

          if (profile.organization && profile.organization.themeColor) {
            setBrokerColor(getIsWhiteLabeled() ? profile.organization.themeColor : undefined);

          }

          const brokerage = localStorage.getItem("brokerage");
          if (getIsWhiteLabeled() && profile.brokerages && profile.brokerages.length > 1 && (!brokerage || brokerage === "")) {
            const sylSubdomain = getSubdomainFromUrl();
            if (sylSubdomain && sylSubdomain !== MsalConstants.CentralRedirectSubdomain) {
              const foundBrokerage = profile.brokerages.find(x => (x.subdomain && x.subdomain.toLocaleLowerCase() === sylSubdomain.toLocaleLowerCase()));
              if (foundBrokerage) {
                localStorage.setItem("brokerage", JSON.stringify(foundBrokerage));
              }
              else {
                // TODO: Handle brokerage subdomain mismatch phase 3
                logout(instance, removeCookie);
              }
            }
            else {
              handleRouteToBrokerages();
            }
          }
          else if (getIsWhiteLabeled() && profile.brokerages && profile.brokerages.length === 1) {
            const sylSubdomain = getSubdomainFromUrl();
            if (process.env.REACT_APP_ENV !== Environments.LOCAL && sylSubdomain && sylSubdomain === MsalConstants.CentralRedirectSubdomain && profile.brokerages[0].useWhitelabelPreferences === false) {
              handleCreateAuthCookies(setCookie);
              window.location.href = `https://builderscapital${getEnvironmentDomain()}/subdomain`;
            }
            else if (process.env.REACT_APP_ENV !== Environments.LOCAL && sylSubdomain && sylSubdomain === MsalConstants.CentralRedirectSubdomain && profile.brokerages[0].subdomain) {
              handleCreateAuthCookies(setCookie);
              window.location.href = `https://${profile.brokerages[0].subdomain.toLocaleLowerCase()}${getEnvironmentDomain()}/subdomain`;
            } 
            else if (sylSubdomain && sylSubdomain !== MsalConstants.CentralRedirectSubdomain && 
            profile.brokerages[0].subdomain && profile.brokerages[0].subdomain.toLocaleLowerCase() !== sylSubdomain.toLocaleLowerCase()) {
              // TODO: Handle brokerage subdomain mismatch phase 3
              logout(instance, removeCookie);
            }
          }

          setCompletedW9(profile.isW9Completed);
          setIsW9MessageVisible(profile.isW9Completed);
          setTimeout(() => {
            setIsW9MessageVisible(true);
          }, 5000);
        }

        if (newAvatarFile) {
          user.avatar = URL.createObjectURL(newAvatarFile);
        }

        setUser(user);
        dispatch({
          type: GlobalConstants.BCStoreReducerCases.SET_USER,
          payload: user,
        });
        
        setIsProfileLoading(false);
        const zendeskScript = document.createElement("script");
        zendeskScript.setAttribute("id", "ze-snippet");
        zendeskScript.setAttribute("src", "https://static.zdassets.com/ekr/snippet.js?key=bc5dd95b-fa8b-45e9-80d5-303e5a34748c");
        if (!getIsWhiteLabeled() && window.location.pathname !== "/welcome" && user?.portal !== GlobalConstants.Portals.LMS) {
          document.body.appendChild(zendeskScript);
        }
      }
      catch (error: any) {
        createErrorResponse("Error", "Caught Error in the getUserAsync call", undefined, "getUserAsync", undefined, error.status, "GET", error);
        handleError();
      }
    };

    getUserAsync();
  }, [
    isUserProfileUpdate,
    setUser,
    setIsMessage,
    setErrorResponse,
    proxyUser,
    handleError,
    setProfile,
    setIsProfileLoading,
    handeGetProfileAssetAsync,
    setIsW9MessageVisible,
    setPermissions,
    setBrokerColor,
    setTenantLogo,
    isCreateNewUser,
    newAvatarFile,
    setSubdomain
  ]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  return (
    <>
      <BCNavbar
        isGreaterThanMobile={isGreaterThanMobile}
        isSidebar={isSidebar}
        setIsSidebar={setIsSidebar}
        isSidebarToggle={isSidebarToggle}
        setIsMessage={setIsMessage}
        setErrorResponse={setErrorResponse}
        user={user}
        proxyUser={proxyUser}
        removeCookie={removeCookie}
        portalPermissionsList={portalPermissionsList}
        completedW9={completedW9}
        isProfileLoading={isProfileLoading}
        permissions={permissions}
        profile={profile}
      />
      <BCSidebar
        isGreaterThanMobile={isGreaterThanMobile}
        isGreaterThanTablet={isGreaterThanTablet}
        isSidebar={isSidebar}
        user={user}
        proxyUser={proxyUser}
        setIsSidebar={setIsSidebar}
        setIsSidebarToggle={setIsSidebarToggle}
        profile={profile}
        isProfileLoading={isProfileLoading}
        setIsModalOpen={setIsModalOpen}
        permissions={permissions}
        isCreateNewUser={isCreateNewUser}
        setIsCreateNewUser={setIsCreateNewUser}
        brokerColor={brokerColor}
        isUserProfileEdit={isUserProfileEdit}
        isUserProfileUpdate={isUserProfileUpdate}
      >
        {!isProfileLoading && <React.Fragment>
          <BCModal
            className="welcome-modal-container"
            isModalOpen={isModalOpen && isFTUE}
            onClose={() => setIsModalOpen(false)}
            onOpen={() => setIsModalOpen(true)}
            size="tiny"
            header="WELCOME!"
            headerClass="welcome-modal"
            content={<React.Fragment>
              <div className="welcome-text"><b>Getting started is as easy as 1, 2, 3</b></div>
              <div className="welcome-text">1. Update your Profile Information and add your picture.</div>
              <div className="welcome-text">2. Complete a W9 Form.</div>
              <div>3. Upload your company's logo and brand colors for easy white-labeling. </div>
            </React.Fragment>}
            contentClass="welcome-modal"
            actions={<Button className="bp-button" icon fluid onClick={() => handleWelcomeMessage()}>Get Started</Button>}
            actionsClass="welcome-modal"
          />
          {successMessage !== "" && <div className={`primary-message ${(isGreaterThanTablet && isSidebar) ? "hide-sidebar" : "sidebar"}`}>
            <BCMessage
              animation={"fly down"}
              duration={750}
              visible={successMessage !== ""}
              transitionOnMount
              styles="full-width"
              negative
              color="green"
              header={"Success!"}
              text={successMessage}
              onDismiss={() => setSuccessMessage("")}
            />
          </div>}
          {children}
        </React.Fragment>}
      </BCSidebar>
    </>
  );
};

export default BCLayout;